import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { InfiniteScrollCustomEvent } from '@ionic/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Events } from 'src/providers/events/events';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { CustomerGuestsApiService } from 'src/shared/services/customer-guests-api.service';
import { Util } from 'src/shared/util/util';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { TelephonePipe } from 'src/shared/pipes/telephone.pipe';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { SharedModule } from 'src/shared/shared.module';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { GuestPlatformBadge, guestPlatformBadge } from 'src/shared/constants/guest-platform-badges';

import {
  CUSTOMER_GUESTS_CHANGED_EVENT,
  CustomerGuest,
  CustomerGuestFilters,
  CustomerGuestPlatform,
} from 'src/models/customer-guest.model';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

import { EditGuestPage } from 'src/pages/customer-guests/edit-guest/edit-guest';

type CustomerGuestFilterKey = keyof CustomerGuestFilters;

const PAGE_SIZE = 25;
const DEFAULT_SEARCH_FILTER: CustomerGuestFilterKey = 'name';

@Component({
  selector: 'customer-guests-tab',
  templateUrl: './customer-guests-tab.component.html',
  styleUrls: ['./customer-guests-tab.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CustomerGuestsTabComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly events = inject(Events);
  private readonly guestsApi = inject(CustomerGuestsApiService);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  readonly windowService = inject(WindowService);

  readonly headers = ['Name', 'Email', 'Phone', 'Platform', 'Added', 'last-column'];
  readonly searchForm: UntypedFormGroup;
  readonly searchFilterOptions: SelectModel[] = [
    { viewValue: 'Name', value: 'name', icon: 'assets/svg/person-outline.svg' },
    { viewValue: 'Email', value: 'email', icon: 'assets/svg/mail-outline.svg' },
    { viewValue: 'Phone', value: 'phone', icon: 'assets/svg/phone-portrait-outline.svg' },
  ];

  readonly pageSize = PAGE_SIZE;
  readonly skeletonRows = Array.from({ length: 14 });
  readonly mobileSkeletonRows = Array.from({ length: 6 });

  guests: CustomerGuest[] = [];
  rows: any[] = [];
  totalCount = 0;
  // Drives tidy-table's infinite-scroll stop check. We can't trust X-Total-* headers (not
  // CORS-exposed), so we expose "one more than loaded" while a full page keeps coming and the
  // exact count once we hit the end — keeping the table's `length >= totalRows` guard in sync.
  totalRows = 0;
  loaded = false;
  isFetching = false;
  hasMore = false;
  errorMessage = '';

  private pageIndex = 1;
  private seenIds = new Set<number>();
  private changedSubscription?: Subscription;
  private lastSearch = '';
  private fetchRequestId = 0;

  constructor() {
    this.searchForm = this.fb.group({
      search: [''],
      filter: [DEFAULT_SEARCH_FILTER],
    });
    this.searchForm.get('search')!.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => this.onSearchChange(value ?? ''));
  }

  async ngOnInit(): Promise<void> {
    this.changedSubscription = this.events.subscribe(CUSTOMER_GUESTS_CHANGED_EVENT, () =>
      this.fetch()
    );
    await this.fetch();
  }

  ngOnDestroy(): void {
    this.changedSubscription?.unsubscribe();
  }

  hasResults(): boolean {
    return this.loaded && this.guests.length > 0;
  }

  isEmpty(): boolean {
    return this.loaded && !this.isFetching && this.guests.length === 0 && !this.lastSearch;
  }

  isEmptySearch(): boolean {
    return this.loaded && !this.isFetching && this.guests.length === 0 && !!this.lastSearch;
  }

  // Desktop tidy-table infinite scroll (same hook bills uses).
  loadMore() {
    this.loadNextPage();
  }

  // Mobile card list infinite scroll.
  async handleInfiniteScroll(event: InfiniteScrollCustomEvent) {
    await this.loadNextPage();
    event?.target?.complete();
  }

  badgeFor(platform: CustomerGuestPlatform): GuestPlatformBadge {
    return guestPlatformBadge(platform);
  }

  goToAddGuest = () => this.openEditGuest('create');

  goToEditGuest(guest: CustomerGuest) {
    this.openEditGuest('edit', guest);
  }

  onTableCellClick(event: { column: string; element: any }) {
    const guest: CustomerGuest | undefined = event.element?.guest;
    if (!guest) {
      return;
    }
    if (event.column === 'last-column') {
      this.confirmDelete(guest);
      return;
    }
    this.goToEditGuest(guest);
  }

  async confirmDelete(guest: CustomerGuest) {
    const translate = new TranslationPipe();
    const alert = await this.util.showConfirmAlert({
      header: translate.transform('Remove Guest'),
      message: translate.transform(
        guest.name
          ? `Are you sure you want to remove ${guest.name}?`
          : 'Are you sure you want to remove this guest?'
      ),
      buttons: [
        { text: translate.transform('Go Back'), role: 'cancel', cssClass: 'secondary' },
        { text: translate.transform('Remove Guest'), role: 'confirm' },
      ],
    });
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') {
      return;
    }
    await this.deleteGuest(guest);
  }

  private async deleteGuest(guest: CustomerGuest) {
    try {
      await firstValueFrom(this.guestsApi.deleteGuest(guest.id));
      this.util.showSuccess('Guest removed');
      await this.fetch();
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Could not remove guest');
    }
  }

  private async openEditGuest(mode: 'create' | 'edit', guest?: CustomerGuest) {
    const params = { mode, guest: guest ?? null };

    if (this.windowService.isDesktopRes) {
      const isPanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      await this.storage.save('dialog-right-side-open', true);
      await this.storage.save('dialog-params', params);
      if (isPanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo('edit-guest', params, EditGuestPage);
      } else {
        this.rightSidePanelService.openRightSidePanel(EditGuestPage);
      }
      return;
    }

    await this.navCtrl.navigateForward('edit-guest', params);
  }

  private onSearchChange(value: string) {
    const trimmed = value.trim();
    if (trimmed === this.lastSearch) {
      return;
    }
    this.lastSearch = trimmed;
    this.fetch();
  }

  onSearchFilterChange(filter: string) {
    this.searchForm.get('filter')!.setValue(filter as CustomerGuestFilterKey, { emitEvent: false });
    if (this.lastSearch) {
      this.fetch();
    }
  }

  get selectedSearchFilterIcon(): string {
    const filter = this.searchForm.get('filter')!.value;
    return this.searchFilterOptions.find((option) => option.value === filter)?.icon || '';
  }

  // Reload from the first page (initial load, search change, or after a CRUD change).
  private async fetch() {
    const requestId = ++this.fetchRequestId;
    this.pageIndex = 1;
    this.seenIds.clear();
    this.guests = [];
    this.rows = [];
    await this.loadPage(false, requestId);
  }

  // Append the next page (infinite scroll). The endpoint paginates server-side; we treat a
  // full page as "there may be more" rather than relying on X-Total-* headers, which aren't
  // reliably exposed to the browser via CORS (the cause of the "only 10 of 29" QA bug).
  private async loadNextPage() {
    if (this.isFetching || !this.hasMore) {
      return;
    }
    this.pageIndex += 1;
    await this.loadPage(true, this.fetchRequestId);
  }

  private async loadPage(append: boolean, requestId: number) {
    this.isFetching = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    try {
      const result = await firstValueFrom(
        this.guestsApi.listGuests({
          page: this.pageIndex,
          perPage: this.pageSize,
          filters: this.currentSearchFilters(),
        })
      );
      if (requestId !== this.fetchRequestId) {
        return;
      }
      const fresh = result.guests.filter((guest) => !this.seenIds.has(guest.id));
      fresh.forEach((guest) => this.seenIds.add(guest.id));
      this.guests = append ? [...this.guests, ...fresh] : fresh;
      this.rows = this.guests.map((guest) => this.toRow(guest));
      this.totalCount = this.guests.length;
      // A full page (and at least one new row) means more may be waiting. A short page, an
      // empty page, or an all-duplicate page (backend ignoring `page`) means we're done.
      this.hasMore = result.guests.length === this.pageSize && fresh.length > 0;
      this.totalRows = this.guests.length + (this.hasMore ? 1 : 0);
    } catch (err: any) {
      if (requestId !== this.fetchRequestId) {
        return;
      }
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load guests';
    } finally {
      if (requestId === this.fetchRequestId) {
        this.isFetching = false;
        this.loaded = true;
        this.cdr.markForCheck();
      }
    }
  }

  private toRow(guest: CustomerGuest): any {
    const badge = this.badgeFor(guest.platform);
    const created = guest.created_at
      ? new ParseDateTimePipe().transform(guest.created_at, 'MMM d, yyyy')
      : '';
    return {
      guest,
      Name: { value: guest.name || '—', initials: true },
      Email: { value: guest.email || '' },
      Phone: { value: this.formatPhone(guest.phone) },
      Platform: { value: badge.label, icon: badge.icon },
      Added: { value: created },
      'last-column': { value: '', action: null, icon: 'assets/svg/trash-outline.svg' },
    };
  }

  formatPhone(phone: string | null): string {
    return phone ? new TelephonePipe().transform(phone, 'format') : '';
  }

  private currentSearchFilters(): CustomerGuestFilters | undefined {
    if (!this.lastSearch) {
      return undefined;
    }
    const filter = this.searchForm.get('filter')!.value as CustomerGuestFilterKey;
    const filters: CustomerGuestFilters = {};
    filters[filter] = this.lastSearch;
    return filters;
  }

  formatAddedLabel(createdAt: string | null | undefined): string {
    if (!createdAt) {
      return '';
    }
    const date = new ParseDateTimePipe().transform(createdAt, 'MMM d, yyyy');
    return `${new TranslationPipe().transform('Added')} ${date}`;
  }
}
